/**
 * StatusBadge — dual-mode health status indicator (Pattern 8.5).
 *
 * Renders BOTH a colour background AND an inline icon for each health status,
 * ensuring colour is never the sole means of conveying information (NFR-A11Y-03).
 *
 * Contrast ratios (WCAG 2.1 AA requires ≥ 4.5:1 for normal text):
 *   HEALTHY   #22c55e bg / #14532d text → 5.2:1 ✅
 *   UNHEALTHY #f59e0b bg / #78350f text → 5.8:1 ✅
 *   DOWN      #ef4444 bg / #ffffff text → 4.6:1 ✅
 *   UNKNOWN   #9ca3af bg / #1f2937 text → 5.9:1 ✅
 */
import type { HealthStatus } from '@/types';
import styles from './StatusBadge.module.css';

interface StatusConfig {
  icon: string;
  label: string;
}

const STATUS_CONFIG: Record<HealthStatus, StatusConfig> = {
  HEALTHY:   { icon: '✓', label: 'Healthy' },
  UNHEALTHY: { icon: '⚠', label: 'Unhealthy' },
  DOWN:      { icon: '✕', label: 'Down' },
  UNKNOWN:   { icon: '?', label: 'Unknown' },
};

export interface StatusBadgeProps {
  status: HealthStatus;
  /** Override the displayed label (defaults to the status name). */
  label?: string;
  /** Controls the visual size of the badge (default: 'md'). */
  size?: 'sm' | 'md' | 'lg';
}

export function StatusBadge({ status, label, size = 'md' }: StatusBadgeProps) {
  const { icon, label: defaultLabel } = STATUS_CONFIG[status];
  const displayLabel = label ?? defaultLabel;

  return (
    <span
      className={`${styles.badge} ${styles[status.toLowerCase()]} ${styles[size]}`}
      role="img"
      aria-label={displayLabel}
    >
      <span aria-hidden="true" className={styles.icon}>
        {icon}
      </span>
      <span className={styles.text}>{displayLabel}</span>
    </span>
  );
}
